import React, { useState, useEffect } from 'react';
import { ArrowRight, Code2, MessageSquare, Zap, Github, LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Landing() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Animated background gradient */}
      <div className="fixed inset-0 opacity-30">
        <div 
          className="absolute w-96 h-96 bg-white rounded-full mix-blend-screen filter blur-3xl opacity-20"
          style={{
            left: `${mousePosition.x - 192}px`,
            top: `${mousePosition.y - 192}px`,
            transition: 'all 0.3s ease-out'
          }}
        />
      </div>

      {/* Navigation */}
      <nav className="relative z-10 border-b border-gray-800 backdrop-blur-sm bg-black/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div 
            className="text-2xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/')}
          >
            RepoChat
          </div>
          <div className="flex items-center gap-4">
            <a href="#features" className="text-sm text-gray-300 hover:text-white transition-colors">
              Features
            </a>
            <button 
              onClick={() => navigate('/login')}
              className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-100 transition-all font-medium"
            >
              <LogIn size={16} />
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative z-10 pt-32 pb-20">
        <div className="max-w-6xl mx-auto px-6">
          {/* Main heading with animation */}
          <div className="text-center space-y-8 mb-16">
            <div className="inline-block">
              <div className="px-4 py-2 rounded-full border border-gray-700 bg-gray-900/50 backdrop-blur-sm text-sm text-gray-300 mb-4 inline-block">
                ✨ Chat with your code
              </div>
            </div>

            <h1 className="text-6xl md:text-7xl font-bold leading-tight">
              <span className="block">Talk to Your</span>
              <span className="block bg-gradient-to-r from-white via-gray-200 to-white bg-clip-text text-transparent">
                Codebase
              </span>
            </h1>

            <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Connect your GitHub repositories and ask intelligent questions about your code. Get instant insights powered by AI, without leaving your browser.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <button
                onClick={() => navigate('/login')}
                className="px-8 py-4 bg-white text-black rounded-lg hover:bg-gray-100 transition-all duration-200 font-semibold flex items-center justify-center gap-2 group"
              >
                Get Started
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="https://github.com/Shubz224/RepoChat"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 border border-gray-700 rounded-lg hover:border-white hover:bg-gray-900/50 transition-all duration-200 font-semibold flex items-center justify-center gap-2"
              >
                <Github size={20} />
                View on GitHub
              </a>
            </div>
          </div>

          {/* Demo preview box */}
          <div className="relative mt-20 max-w-4xl mx-auto">
            <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-gray-800/10 rounded-2xl blur-2xl" />
            <div className="relative border border-gray-800 rounded-2xl bg-black/80 backdrop-blur-xl p-8 overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
              
              <div className="space-y-4">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <div className="space-y-3 pt-4">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-white/10" />
                    <div className="flex-1">
                      <div className="h-2 bg-white/20 rounded w-3/4 mb-2" />
                      <div className="h-2 bg-white/10 rounded w-1/2" />
                    </div>
                  </div>
                  <div className="flex gap-3 ml-12">
                    <div className="flex-1">
                      <div className="h-2 bg-white/10 rounded w-5/6 mb-2" />
                      <div className="h-2 bg-white/20 rounded w-3/4" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="relative z-10 py-20 border-t border-gray-800">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16">Why Choose RepoChat</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="group">
              <div className="h-full border border-gray-800 rounded-xl p-8 hover:border-gray-600 transition-all duration-300 bg-gradient-to-br from-gray-900/50 to-black hover:from-gray-900 hover:to-gray-900/50">
                <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center mb-6 group-hover:bg-white/20 transition-colors">
                  <Code2 size={24} />
                </div>
                <h3 className="text-xl font-semibold mb-3">Instant Understanding</h3>
                <p className="text-gray-400">
                  Understand complex codebases instantly. Ask questions about functions, architecture, or patterns in natural language.
                </p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="group">
              <div className="h-full border border-gray-800 rounded-xl p-8 hover:border-gray-600 transition-all duration-300 bg-gradient-to-br from-gray-900/50 to-black hover:from-gray-900 hover:to-gray-900/50">
                <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center mb-6 group-hover:bg-white/20 transition-colors">
                  <MessageSquare size={24} />
                </div>
                <h3 className="text-xl font-semibold mb-3">Natural Conversation</h3>
                <p className="text-gray-400">
                  Chat with your code just like talking to a colleague. Get detailed explanations and suggestions instantly.
                </p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="group">
              <div className="h-full border border-gray-800 rounded-xl p-8 hover:border-gray-600 transition-all duration-300 bg-gradient-to-br from-gray-900/50 to-black hover:from-gray-900 hover:to-gray-900/50">
                <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center mb-6 group-hover:bg-white/20 transition-colors">
                  <Zap size={24} />
                </div>
                <h3 className="text-xl font-semibold mb-3">Lightning Fast</h3>
                <p className="text-gray-400">
                  Powered by AI and optimized indexing. Get answers in seconds, not minutes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative z-10 py-20 border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Chat with Your Code?</h2>
          <p className="text-xl text-gray-400 mb-8">
            Connect your GitHub repository in seconds and start exploring your codebase like never before.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black rounded-lg hover:bg-gray-100 transition-all duration-200 font-semibold group"
          >
            Get Started Now
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 border-t border-gray-800 py-8 text-center text-gray-500">
        <p>© 2025 RepoChat. Ask questions. Understand faster.</p>
      </footer>
    </div>
  );
}